import React, { useState, useRef, useEffect } from 'react'
import {
  ChevronDown,
  FolderOpen,
  Megaphone,
  Calendar,
  Briefcase,
} from 'lucide-react'
import { AlbumType } from './types/models'

interface AlbumSelectProps {
  value: AlbumType
  onChange: (value: AlbumType) => void
  t: (key: string) => string
  displayMode?: 'select' | 'text'
}

const AlbumSelect: React.FC<AlbumSelectProps> = ({
  value,
  onChange,
  t,
  displayMode = 'select',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const options = [
    {
      value: 'announce',
      label: t('album-announces'),
      icon: <Megaphone className='w-4 h-4 sm:w-5 sm:h-5' />,
    },
    {
      value: 'event',
      label: t('album-events'),
      icon: <Calendar className='w-4 h-4 sm:w-5 sm:h-5' />,
    },
    {
      value: 'jobOffer',
      label: t('album-job-offers'),
      icon: <Briefcase className='w-4 h-4 sm:w-5 sm:h-5' />,
    },
  ]

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleDropdown = () => setIsOpen(!isOpen)

  const handleOptionClick = (value: string) => {
    onChange(value as AlbumType)
    setIsOpen(false)
  }

  const selectedOption = options.find(opt => opt.value === value)

  if (displayMode === 'text') {
    return (
      <div className='flex flex-col sm:flex-row items-start sm:items-center  p-2 sm:px-4 sm:py-2'>
        <div className='hidden sm:flex items-center gap-2 whitespace-nowrap mb-1 sm:mb-0'>
          <FolderOpen className='w-4 h-4 sm:w-5 sm:h-5 text-white' />
        </div>
        <div className='flex items-center gap-2 text-base sm:text-base text-white'>
          <span className='truncate ml-2'>{selectedOption?.label}</span>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={dropdownRef}
      className='relative flex flex-col sm:flex-row items-start sm:items-center gap-2 p-2 sm:px-4 sm:py-2'
    >
      <div className='hidden sm:flex items-center gap-2 whitespace-nowrap mb-1 sm:mb-0'>
        <FolderOpen className='w-4 h-4 sm:w-5 sm:h-5 text-redpink' />
      </div>

      <div className='relative w-full sm:max-w-xs'>
        <div
          onClick={toggleDropdown}
          className='w-full bg-redpink relative hover:bg-redpink/80 transition-colors
            border-white/10 hover:border-white/20
            rounded-md px-3 sm:px-4 py-1 sm:py-1.5 pl-8 sm:pl-10
            text-white text-xs sm:text-sm cursor-pointer
            flex items-center gap-2'
        >
          <div className='absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 text-anthracite'>
            {selectedOption?.icon}
          </div>
          <span className='truncate'>{selectedOption?.label}</span>
          <ChevronDown
            className={`ml-auto w-4 h-4 sm:w-5 sm:h-5 text-white/70 transition-transform duration-200
              ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>

        {isOpen && (
          <ul
            className='absolute w-full mt-1 max-h-[160px] sm:max-h-[180px] overflow-auto
            bg-anthracite border border-white/10 text-white rounded-md
            shadow-lg shadow-black/30 z-50
            scrollbar-thin scrollbar-thumb-redpink/50 hover:scrollbar-thumb-redpink
            scrollbar-track-anthraciteLight'
          >
            {options.map(option => (
              <li
                key={option.value}
                className={`px-3 sm:px-4 py-1.5 sm:py-2 cursor-pointer
                  hover:bg-anthraciteLight transition-all
                  flex items-center gap-2
                  border-b border-white/5 last:border-none
                  text-xs sm:text-sm
                  ${option.value === value ? 'bg-anthraciteLight' : ''}
                  ${option.value === value ? 'text-redpink' : 'text-white'}`}
                onClick={() => handleOptionClick(option.value)}
              >
                {option.icon}
                <span className='truncate'>{option.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default AlbumSelect
